import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { groupBy, isEmpty, join, map, mapValues, size, toPairs, uniq } from 'es-toolkit/compat';
import React from 'react';

import CopyableText from 'components/atoms/CopyableText';
import { LinkToSlideViewerComponent } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import ClassesToAnnotateList from 'components/StudyDashboard/AnnotationAssignment/ClassesToAnnotateList';
import { AnnotationAssignment, TodoDefinition } from 'interfaces/annotation';
import { humanize } from 'utils/helpers';
import { useFilterAndFlattenAnnotationAssignments } from './helpers';

export interface Stats extends AnnotationAssignment, TodoDefinition {}

function StatsRow(props: { stats: Stats }) {
  const { stats } = props;
  const [open, setOpen] = React.useState(false);

  const annotationsByStatus = groupBy(stats?.annotations, 'workingState');
  const numAnnotationsByStatus = mapValues(annotationsByStatus, size);
  const options = stats?.options;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{stats?.todo}</TableCell>
        <TableCell>
          {map(numAnnotationsByStatus, (count, status) => (
            <Typography variant="body2" key={status}>
              {humanize(status)}: {count}
            </Typography>
          ))}
        </TableCell>
        <TableCell>{size(options)} Options</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Details
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Working State</TableCell>
                    <TableCell>Slide IDs</TableCell>
                    <TableCell>Options</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(toPairs(annotationsByStatus), ([workingState, annotations], index) => (
                    <TableRow key={workingState}>
                      <TableCell>
                        <Box sx={{ maxHeight: 100, overflow: 'auto' }}>{humanize(workingState)}</Box>
                      </TableCell>
                      <TableCell>
                        <CopyableText
                          sx={{ maxHeight: 100, overflow: 'auto', whiteSpace: 'unset' }}
                          copyWithButton="right"
                          textForCopy={join(uniq(map(annotations, 'slideId')), '\n')}
                        >
                          {map(uniq(map(annotations, 'slideId')), (slideId) => (
                            <LinkToSlideViewerComponent
                              key={slideId}
                              target="_blank"
                              slideId={slideId}
                              caseStudyId={stats?.studyId}
                              activeAnnotationAssignment={stats?.annotationAssignmentId}
                            />
                          ))}
                        </CopyableText>
                      </TableCell>
                      {index === 0 && (
                        <TableCell rowSpan={size(annotationsByStatus)}>
                          <Box sx={{ maxHeight: 100 * size(annotationsByStatus), overflow: 'auto' }}>
                            <ClassesToAnnotateList classesToAnnotate={options || []} />
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export interface Props {
  annotationAssignments: AnnotationAssignment[];
  selectedAssignmentIds: number[];
  selectedSlideIds?: string[];
}

export const AnnotationStatsTable: React.FC<Props> = ({
  annotationAssignments,
  selectedAssignmentIds,
  selectedSlideIds,
}) => {
  const filteredAndFlattenedAnnotationAssignments = useFilterAndFlattenAnnotationAssignments({
    annotationAssignments,
    selectedAssignmentIds,
    selectedSlideIds,
  });

  return !isEmpty(filteredAndFlattenedAnnotationAssignments) ? (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell>Assignment</TableCell>
            <TableCell>Slides</TableCell>
            <TableCell>Options</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {map(filteredAndFlattenedAnnotationAssignments, (stats) => (
            <StatsRow key={stats?.annotationAssignmentId} stats={stats} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  ) : (
    <Typography variant="body2">No annotation assignments selected</Typography>
  );
};
